import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { publicApi } from '../api/public';
import { discountsApi } from '../api/discounts';
import DiscountCard from '../components/discounts/DiscountCard';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { ArrowRight, Award, ShoppingBag, Tag } from 'lucide-react';
import { Store } from 'lucide-react';

const Home = () => {

  // Завантаження даних для головної сторінки з кешуванням
  const { data: homeData, isLoading: homeLoading } = useQuery({
    queryKey: ['homePageData'],
    queryFn: () => publicApi.getHomePageData().then(res => res.data),
    staleTime: 10 * 60 * 1000, // Збільшено до 10 хвилин
    cacheTime: 30 * 60 * 1000, // 30 хвилин в кеші
    refetchOnWindowFocus: false,
    retry: 2,
  });

  // Завантажуємо всіх партнерів
  const { data: allPartners } = useQuery({
    queryKey: ['allPartners'],
    queryFn: () => publicApi.getPartners().then(res => res.data),
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  // Окремо завантажуємо останні знижки з оптимізацією
  const { data: recentDiscounts, isLoading: discountsLoading } = useQuery({
    queryKey: ['recentDiscounts', 6],
    queryFn: () => discountsApi.getRecentDiscounts(6).then(res => res.data),
    staleTime: 5 * 60 * 1000, // 5 хвилин
    cacheTime: 15 * 60 * 1000, // 15 хвилин в кеші
    refetchOnWindowFocus: false,
    retry: 2,
  });

  // Мемоізація обчислень
  const statistics = useMemo(() => {
    if (!homeData?.statistics) return null;
    return [
      { value: homeData.statistics.partnersCount, label: 'Партнерів' },
      { value: homeData.statistics.discountsCount, label: 'Активних знижок' },
      { value: '24/7', label: 'Доступність' },
      { value: 'Безкоштовно', label: 'Для членів профспілки' }
    ];
  }, [homeData?.statistics]);

  const isLoading = homeLoading || discountsLoading;

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Герой-секція з оптимізованим зображенням */}
      <div className="bg-gradient-to-r from-primary-700 to-primary-900 rounded-xl overflow-hidden shadow-lg mb-10">
        <div className="md:flex">
          <div className="md:w-1/2 p-8 md:p-12 flex flex-col justify-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Програма лояльності для співробітників Чернівецького національного університету імені Юрія Федьковича
            </h1>
            <p className="text-primary-100 mb-6 text-lg">
              Отримуйте знижки та спеціальні пропозиції від партнерів. Економте ваші кошти разом з Профспілковою організацією університету!
            </p>
            <div className="flex flex-wrap gap-3">
              <Button 
                to="/discounts" 
                variant="secondary" 
                size="lg" 
                icon={<ShoppingBag size={18} />}
              >
                Переглянути знижки
              </Button>
              <Button 
                to="/profile" 
                variant="outline" 
                size="lg" 
                className="bg-transparent border-white text-white hover:bg-white/10"
              >
                Особистий кабінет
              </Button>
            </div>
          </div>
          <div className="md:w-1/2 p-6 md:p-8 flex items-center justify-center">
            <img
              src="/logo.png"
              alt="ПрофБонус"
              className="w-full max-w-md h-auto object-contain"
              loading="eager"
              fetchpriority="high"
              decoding="sync"
            />
          </div>
        </div>
      </div>

      {/* Статистика з мемоізацією */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
          {statistics.map((stat, index) => (
            <Card key={index} className="text-center p-6">
              <div className="text-3xl font-bold text-primary-600 mb-2">{stat.value}</div>
              <div className="text-gray-600">{stat.label}</div>
            </Card>
          ))}
        </div>
      )}

      {/* Останні знижки з оптимізованим завантаженням */}
      <div className="mb-10">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center">
            <Tag size={24} className="text-primary-600 mr-2" />
            Останні знижки
          </h2>
          <Button 
            to="/discounts" 
            variant="outline" 
            icon={<ArrowRight size={16} />}
          >
            Всі знижки
          </Button>
        </div>
        
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 h-48 rounded-t-lg"></div>
                <div className="bg-white p-4 rounded-b-lg shadow-sm">
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2 w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : recentDiscounts && recentDiscounts.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {recentDiscounts.map(discount => (
                <DiscountCard
                  key={discount.id}
                  discount={discount}
                  showExpirationWarning={true}
                />
              ))}
            </div>
            <div className="flex justify-center mt-8">
              <Button
                to="/discounts"
                variant="primary"
                size="lg"
                icon={<ArrowRight size={18} />}
              >
                Переглянути всі знижки
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4">
              <Tag size={48} className="mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">Поки що немає нових знижок</h3>
            <p className="text-gray-500 mb-4">Незабаром з'являться нові пропозиції від наших партнерів!</p>
            <Button to="/discounts" variant="outline">
              Переглянути всі знижки
            </Button>
          </div>
        )}
      </div>

      {/* Партнери — бігуча строка */}
      {allPartners && allPartners.length > 0 && (
        <div className="mb-10">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center">
              <Store size={24} className="text-primary-600 mr-2" />
              Наші партнери
            </h2>
          </div>

          {/* Десктоп — бігуча строка */}
          <div className="hidden md:block marquee-container rounded-xl bg-white/50 py-6">
            <div
              className="marquee-track"
              style={{
                animationDuration: `${Math.max(allPartners.length * 4, 20)}s`,
              }}
            >
              {[...allPartners, ...allPartners].map((partner, index) => (
                <Link
                  key={`${partner.id}-${index}`}
                  to={`/discounts?partnerId=${partner.id}`}
                  className="flex-shrink-0 mx-4 bg-white px-8 py-4 rounded-xl shadow-sm hover:shadow-lg transition duration-200 flex flex-col items-center justify-center h-32 w-52"
                >
                  {partner.logoUrl ? (
                    <img
                      src={partner.logoUrl}
                      alt={partner.name}
                      className="max-h-20 max-w-full object-contain"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <span className="text-center font-semibold text-gray-700 text-base leading-tight">
                      {partner.name}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </div>

          {/* Мобільний — таблиця партнерів */}
          <div className="md:hidden flex flex-wrap justify-center gap-3">
            {allPartners.map(partner => (
              <Link
                key={partner.id}
                to={`/discounts?partnerId=${partner.id}`}
                className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition duration-200 flex flex-col items-center justify-center h-28 w-[calc(50%-0.375rem)]"
              >
                {partner.logoUrl ? (
                  <img
                    src={partner.logoUrl}
                    alt={partner.name}
                    className="max-h-16 max-w-full object-contain"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <span className="text-center font-semibold text-gray-700 text-sm leading-tight">
                    {partner.name}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Про програму - статичний контент без оптимізації */}
      <div className="bg-white rounded-lg shadow-sm p-6 md:p-8 mb-10">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Про програму "ПрофБонус"</h2>
        <div className="prose max-w-none">
          <p>
            Програма лояльності "ПрофБонус" створена профспілковою організацією Чернівецького національного університету імені Юрія Федьковича для надання додаткових переваг співробітникам університету.
          </p>
          <p>
            Учасники програми отримують доступ до ексклюзивних знижок та спеціальних пропозицій від партнерів у різних сферах: від харчування та відпочинку до медичних послуг та освіти.
          </p>
          <p>
            Для отримання знижки достатньо показати QR-код або штрих-код з мобільного телефону при здійсненні покупки чи замовленні послуги у партнерів програми.
          </p>
          <div className="bg-primary-50 p-4 rounded-lg border border-primary-100 my-4">
            <h3 className="flex items-center text-primary-800 font-medium">
              <Award size={20} className="mr-2 text-primary-600" />
              Переваги програми:
            </h3>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Ексклюзивні знижки для співробітників університету</li>
              <li>Широкий спектр партнерів у різних сферах</li>
              <li>Зручне використання через мобільний телефон</li>
              <li>Інформація про нові знижки та спеціальні пропозиції</li>
              <li>Аналітика власних заощаджень</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Як працює програма */}
      <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Як працює програма</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex flex-col items-center text-center">
            <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mb-4">
              <span className="text-primary-600 text-2xl font-bold">1</span>
            </div>
            <h3 className="text-lg font-medium mb-2">Авторизація</h3>
            <p className="text-gray-600">Увійдіть в систему за допомогою корпоративної пошти ЧНУ або авторизованої адреси.</p>
          </div>
          <div className="flex flex-col items-center text-center">
            <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mb-4">
              <span className="text-primary-600 text-2xl font-bold">2</span>
            </div>
            <h3 className="text-lg font-medium mb-2">Вибір знижки</h3>
            <p className="text-gray-600">Оберіть знижку з каталогу та ознайомтесь з умовами її отримання.</p>
          </div>
          <div className="flex flex-col items-center text-center">
            <div className="bg-primary-100 rounded-full w-16 h-16 flex items-center justify-center mb-4">
              <span className="text-primary-600 text-2xl font-bold">3</span>
            </div>
            <h3 className="text-lg font-medium mb-2">Використання</h3>
            <p className="text-gray-600">Покажіть QR-код або штрих-код партнеру для отримання знижки при оплаті.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
